import type { MouseEvent, ReactNode } from "react";
import { LastUpdated } from "@/components/site/LastUpdated";
import { Layout, Section, Eyebrow, Breadcrumbs } from "@/components/site/Layout";
import { PricingGrid } from "@/components/site/Pricing";
import { FAQSection } from "@/components/site/FAQSection";
import { HubSpotForm } from "@/components/site/HubSpotForm";
import { WebsiteCheck } from "@/components/site/WebsiteCheck";
import { ukPricingTiers, lowestPlanPrice, highestPlanPrice } from "@/data/pricing";
import { ukPages, ukHreflangLinks, UK_TRUST_LINE, UK_WHATSAPP_URL, type UkPage, type UkBlock } from "@/data/uk-pages";
import { jsonLdScript, ogImageFor, SITE_URL } from "@/lib/seo";

const linkCls = "text-gold-deep underline underline-offset-4";

/** Renders **bold** and [text](href) inline markup from the supplied copy. */
function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return <>{parts.map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="text-foreground">{bold[1]}</strong>;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return <a key={i} href={link[2]} className={linkCls}>{link[1]}</a>;
    return part;
  })}</>;
}

function Blocks({ blocks }: { blocks: UkBlock[] }) {
  return <div className="mt-6 max-w-4xl space-y-5">{blocks.map((b, i) => {
    if ("p" in b) return <p key={i} className="text-lg leading-relaxed text-muted-foreground"><Inline text={b.p} /></p>;
    if ("ul" in b) return <ul key={i} className="list-disc space-y-3 pl-6 text-lg leading-relaxed text-muted-foreground">{b.ul.map((li) => <li key={li}><Inline text={li} /></li>)}</ul>;
    if ("table" in b) return <div key={i} className="overflow-x-auto rounded-lg border border-border bg-background overscroll-x-contain" role="region" aria-label="Comparison table" tabIndex={0}>
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="bg-secondary/60"><tr>{b.table.headers.map((h, j) => <th key={j} scope="col" className="p-4 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{b.table.rows.map((row, j) => <tr key={j} className="border-t border-border">{row.map((cell, k) => <td key={k} className="p-4 text-foreground"><Inline text={cell} /></td>)}</tr>)}</tbody>
      </table>
    </div>;
    return <div key={i} className="grid max-w-none gap-5 md:grid-cols-2">{b.h3s.map((g) => (
      <article key={g.h3} className="rounded-lg border border-border bg-background p-6">
        <h3 className="text-xl font-semibold">{g.h3}</h3>
        {g.p.map((p) => <p key={p} className="mt-3 leading-relaxed text-muted-foreground"><Inline text={p} /></p>)}
      </article>
    ))}</div>;
  })}</div>;
}

function smoothHash(event: MouseEvent<HTMLElement>) {
  const anchor = (event.target as HTMLElement).closest?.("a[href^='#']");
  if (!anchor) return;
  const id = anchor.getAttribute("href")!.slice(1);
  const target = document.getElementById(id);
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  history.replaceState(history.state, "", `#${id}`);
}

export function ukHead(path: UkPage["path"]) {
  const page = ukPages[path];
  const url = `${SITE_URL}${page.path}`;
  const og = ogImageFor("service", page.ogSlug);
  const faq = page.sections.find((s) => s.kind === "faq");
  const schemas = [
    { "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#webpage`, name: page.title, description: page.description, url, inLanguage: "en-GB" },
    {
      "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`, name: page.h1, serviceType: page.serviceType, url,
      provider: { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "PageOne Lawyers", url: `${SITE_URL}/` },
      areaServed: { "@type": "Country", name: "United Kingdom" },
      audience: { "@type": "BusinessAudience", audienceType: "Solicitors and law firms in the United Kingdom" },
      offers: { "@type": "AggregateOffer", priceCurrency: "GBP", lowPrice: lowestPlanPrice, highPrice: highestPlanPrice, offerCount: "6" },
    },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: page.crumb, item: url },
    ] },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: (faq && faq.kind === "faq" ? faq.items : []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ];
  return {
    meta: [
      { title: page.title },
      { name: "description", content: page.description },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: page.title },
      { property: "og:description", content: page.description },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_GB" },
      { property: "og:image", content: og },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: `${page.crumb} | PageOne Lawyers` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: page.title },
      { name: "twitter:description", content: page.description },
      { name: "twitter:image", content: og },
    ],
    links: [{ rel: "canonical", href: url }, ...(page.hreflangUs ? ukHreflangLinks(page.hreflangUs, url) : [])],
    scripts: schemas.map(jsonLdScript),
  };
}

function Band({ shaded, children, id }: { shaded: boolean; children: ReactNode; id?: string }) {
  const inner = <Section id={id} className={id ? "scroll-mt-24" : ""}>{children}</Section>;
  return shaded ? <div className="border-y border-border bg-secondary/40">{inner}</div> : inner;
}

export function UkServicePage({ path }: { path: UkPage["path"] }) {
  const page = ukPages[path];
  let band = 0;
  return (
    <Layout>
      <div onClickCapture={smoothHash}>
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-7xl px-5 py-16 sm:px-6 sm:py-20">
            <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: page.crumb }]} />
            <Eyebrow><span className="text-[var(--gold)]">FOR UK FIRMS</span></Eyebrow>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">{page.h1}</h1><LastUpdated />
            <p className="mt-6 max-w-3xl text-xl text-primary-foreground/85">{page.intro}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href="#audit-form" className="inline-flex w-full items-center justify-center rounded-md bg-[var(--gold)] px-6 py-3 text-center text-sm font-semibold text-[var(--navy-deep)] transition hover:bg-[var(--gold-deep)] sm:w-auto">{page.primaryCta}</a>
              <a href="#pricing" className="inline-flex w-full items-center justify-center rounded-md border border-white/30 px-6 py-3 text-center text-sm font-semibold text-white transition hover:bg-white/10 sm:w-auto">See UK pricing</a>
            </div>
            <p className="mt-5 max-w-3xl text-xs text-primary-foreground/65">{UK_TRUST_LINE}</p>
          </div>
        </section>

        {page.sections.map((s, i) => {
          if (s.kind === "websiteCheck") return <WebsiteCheck key={i} />;
          if (s.kind === "faq") return <FAQSection key={i} tone="muted" title={s.h2} items={s.items} />;
          if (s.kind === "form") return (
            <section key={i} id="audit-form" className="scroll-mt-24 bg-hero-gradient text-primary-foreground">
              <div className="mx-auto max-w-3xl px-5 py-14 sm:px-6 sm:py-20">
                <h2 className="font-display text-3xl font-semibold sm:text-4xl">{s.h2}</h2>
                <p className="mt-4 text-lg text-primary-foreground/80">{s.intro}</p>
                <div className="mt-8 overflow-hidden rounded-lg bg-card p-2 shadow-lg sm:p-3 md:p-4">
                  <HubSpotForm className="hs-form-on-light" lazy />
                </div>
                <p className="mt-4 text-center text-sm text-primary-foreground/70">Prefer to chat? <a href={UK_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-4">Message us on WhatsApp</a></p>
              </div>
            </section>
          );
          if (s.kind === "more") return (
            <Section key={i}>
              <h2 className="mb-6 text-3xl font-semibold">More Services for UK Firms</h2>
              <div className="grid gap-5 sm:grid-cols-2">
                {s.cards.map((c) => <a key={c.href} href={c.href} className="group rounded-xl border border-border bg-card p-7 transition hover:border-gold hover:shadow-[var(--shadow-card)]"><h3 className="text-xl font-semibold">{c.title}</h3><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.description}</p></a>)}
              </div>
              <p className="mt-6 text-sm text-muted-foreground"><Inline text={s.usLine} /></p>
            </Section>
          );
          const shaded = band % 2 === 1;
          if (s.kind !== "content" || !s.added) band++;
          if (s.kind === "pricing") return (
            <Band key={i} shaded={shaded} id="pricing">
              <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{s.h2}</h2>
              <p className="mt-4 max-w-4xl text-lg text-muted-foreground">{s.intro}</p>
              <div className="mt-10"><PricingGrid tiers={ukPricingTiers} ctaTo="#audit-form" /></div>
              <p className="mt-6 text-sm font-semibold text-foreground">No setup fees.</p>
              {s.after && <p className="mt-3 text-lg text-muted-foreground">{s.after}</p>}
            </Band>
          );
          return (
            <Band key={i} shaded={shaded}>
              <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{s.h2}</h2>
              <Blocks blocks={s.blocks} />
            </Band>
          );
        })}
      </div>
    </Layout>
  );
}


